import moment from 'moment-timezone';
import { string, object, mixed, bool, number, array } from 'yup';

import { CRAWLER_PERIOD_TYPES } from '../../../../constants/crawlerConstants';
import i18n from '../../../../i18n';
import { sentenceCase } from '../../../../utils/utils';

// Return schema as a function to capture i18n language setting
const argsSchema = () =>
  // This circle validation is to make sure that the user fill in both name and val or neither. Basically a XNOR gate validation
  object().shape(
    {
      name: string().when('val', {
        is: (v) => v !== '',
        then: (schema) =>
          schema.required(
            i18n.t('bothCrawlArgsRequired', { ns: 'validations' })
          ),
        otherwise: (schema) => schema,
      }),
      val: string().when('name', {
        is: (v) => v !== '',
        then: (schema) =>
          schema.required(
            i18n.t('bothCrawlArgsRequired', { ns: 'validations' })
          ),
        otherwise: (schema) => schema,
      }),
    },
    ['name', 'val']
  );

// Return schema as a function to capture i18n language setting
export const schema = () =>
  object().shape(
    {
      jobType: string().required(
        sentenceCase(
          i18n.t('selectRequired', {
            ns: 'validations',
            field: i18n.t('availableJob', { ns: 'fields' }),
          })
        )
      ),
      jobName: string()
        .required(
          sentenceCase(
            i18n.t('inputRequired', {
              ns: 'validations',
              field: i18n.t('jobName', { ns: 'fields' }),
            })
          )
        )
        .max(50, i18n.t('maxLength', { ns: 'validations', max: '50' })),
      user: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('user', { ns: 'fields' }),
          })
        )
      ),
      jobDescription: string().max(
        255,
        i18n.t('maxLength', { ns: 'validations', max: '255' })
      ),
      seeds: string()
        .required(
          sentenceCase(
            i18n.t('inputRequired', {
              ns: 'validations',
              field: i18n.t('seeds', { ns: 'fields' }),
            })
          )
        )
        .test(
          'noDuplicates',
          i18n.t('duplicate', {
            ns: 'errors',
            item: i18n.t('stepper.seed', { ns: 'crawler' }),
          }),
          (val) => {
            if (!val) return;
            const seedArr = val.split('\n');
            const uniqueSeeds = new Set(seedArr);
            return [...uniqueSeeds].length === seedArr.length;
          }
        ),
      subseeds: mixed().test(
        'matchesSeeds',
        i18n.t('subseedContainsSeed', { ns: 'validations' }),
        (val, ctx) => {
          if (!val) return true;
          const seeds = ctx.parent.seeds;
          const seedArr = seeds.length > 0 && seeds.split('\n');
          const subseedArr = val.split('\n');

          const match = subseedArr.filter((subseed) =>
            seedArr.some((seed) => subseed.includes(seed))
          );
          return match.length === subseedArr.length;
        }
      ),
      includeInCrawl: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('includeInCrawl', { ns: 'fields' }),
          })
        )
      ),
      includeInIndex: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('includeInIndex', { ns: 'fields' }),
          })
        )
      ),
      excludeFromCrawl: string(),
      excludeFromIndex: string(),
      excludeContentFromIndex: string(),
      instance: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('instanceName', { ns: 'fields' }),
          })
        )
      ),
      collection: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('collectionName', { ns: 'fields' }),
          })
        )
      ),
      uniqueKey: string().required(
        sentenceCase(i18n.t('noUniqueKey', { ns: 'validations' }))
      ),
      updateHandler: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('updateHandler', { ns: 'fields' }),
          })
        )
      ),
      removeHandler: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('removeHandler', { ns: 'fields' }),
          })
        )
      ),
      statusHandler: string().required(
        sentenceCase(
          i18n.t('inputRequired', {
            ns: 'validations',
            field: i18n.t('statusHandler', { ns: 'fields' }),
          })
        )
      ),
      commitAfterJob: bool().when('commitTime', {
        is: (val) => val === undefined,
        then: (shema) =>
          shema.oneOf(
            [true],
            i18n.t('commitTimeOrAfterJob', { ns: 'validations' })
          ),
        otherwise: (shema) => shema.oneOf([true, false]),
      }),
      commitTime: number().when('commitAfterJob', {
        is: (val) => val === false,
        then: (schema) =>
          schema
            .min(5000, i18n.t('minCommitTime', { ns: 'validations' }))
            .transform((v, o) => (o === '' ? undefined : v))
            .required(i18n.t('commitTimeOrAfterJob', { ns: 'validations' })),
        otherwise: (schema) =>
          schema
            .min(5000, i18n.t('minCommitTime', { ns: 'validations' }))
            .transform((v, o) => (o === '' ? undefined : v)),
      }),
      crawlArgs: array().of(argsSchema()),
      periodType: string(),
      periodTime: string().when('periodType', {
        is: (v) => v !== CRAWLER_PERIOD_TYPES.MANUAL.value,
        then: (schema) =>
          schema
            .test(
              'is-time',
              sentenceCase(
                i18n.t('invalid', {
                  ns: 'validations',
                  field: i18n.t('timePeriod', { ns: 'fields' }),
                })
              ),
              (time) => moment(time, 'HH:mm', true).isValid()
            )
            .required(
              sentenceCase(
                i18n.t('inputRequired', {
                  ns: 'validations',
                  field: i18n.t('timePeriod', { ns: 'fields' }),
                })
              )
            ),
        otherwise: (schema) => schema,
      }),
      timezone: string().when('periodType', {
        is: (v) => v !== CRAWLER_PERIOD_TYPES.MANUAL.value,
        then: (schema) =>
          schema.required(
            sentenceCase(
              i18n.t('inputRequired', {
                ns: 'validations',
                field: i18n.t('timezone', { ns: 'fields' }),
              })
            )
          ),
        otherwise: (schema) => schema,
      }),
      day: string().when('periodType', {
        is: (v) => v === CRAWLER_PERIOD_TYPES.WEEKLY.value,
        then: (schema) =>
          schema.required(
            sentenceCase(
              i18n.t('inputRequired', {
                ns: 'validations',
                field: i18n.t('day', { ns: 'fields' }),
              })
            )
          ),
        otherwise: (schema) => schema,
      }),
      date: string().when('periodType', {
        is: (v) => v === CRAWLER_PERIOD_TYPES.MONTHLY.value,
        then: (schema) =>
          schema.required(
            sentenceCase(
              i18n.t('inputRequired', {
                ns: 'validations',
                field: i18n.t('date', { ns: 'fields' }),
              })
            )
          ),
        otherwise: (schema) => schema,
      }),
    },
    ['commitAfterJob', 'commitTime']
  );
